// iter67 — Universal branded print helper.
// Any page can call: await openBrandedPrintWindow({ title, subtitle, body, orientation, qrUrl, footerNote })
// to open a popup with a fully-styled, brand-colored, logo-headered, print-ready document.
//
// All app reports / tickets / certificates / labels should call this to ensure
// every printed page carries the company's logo, colors, and tagline.
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

// Cache branding for 60s so repeated print-from-list doesn't slam the API
let _brandingCache = null;
let _brandingTs = 0;

export async function getBrandingCached() {
  if (_brandingCache && Date.now() - _brandingTs < 60_000) return _brandingCache;
  try {
    const token = localStorage.getItem('token');
    const r = await axios.get(`${API}/branding`, token ? { headers: { Authorization: `Bearer ${token}` } } : {});
    _brandingCache = r.data || {};
    _brandingTs = Date.now();
  } catch (_) {
    _brandingCache = {};
    _brandingTs = Date.now();
  }
  return _brandingCache;
}

// iter145 — shared letterhead (logo, company, address, contact, tax #) + page footer from backend
export async function getLetterhead() {
  try {
    const token = localStorage.getItem('token');
    const r = await axios.get(`${API}/branding/letterhead`, {
      params: { printed_at: `${new Date().toISOString().slice(0, 16).replace('T', ' ')} UTC` },
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return r.data || {};
  } catch (_) {
    return { header_html: '', css: '' };
  }
}

/** Standard header row: letterhead left, document title right. */
export const letterheadHeader = (lh, title, subtitle = '', extra = '') => `
  <div class="lh-row" style="display:flex;justify-content:space-between;align-items:flex-start;gap:16px;border-bottom:3px solid ${lh.primary_color || '#0f172a'};padding-bottom:12px;margin-bottom:16px;">
    <div>${lh.header_html || ''}</div>
    <div style="text-align:right">
      ${title ? `<div style="font-size:22px;font-weight:800;letter-spacing:0.04em;color:${lh.primary_color || '#0f172a'};text-transform:uppercase">${escapeHtml(title)}</div>` : ''}
      ${subtitle ? `<div style="font-family:ui-monospace,Menlo,Consolas,monospace;color:#475569;font-size:12px;margin-top:4px">${escapeHtml(subtitle)}</div>` : ''}
      ${extra}
    </div>
  </div>`;

/**
 * Open any full-page HTML document with the shared letterhead.
 * The html may contain <!--LETTERHEAD--> where the header row goes (defaults to just after <body>).
 */
export async function openLetterheadWindow(html, { title = '', subtitle = '', extra = '', features = 'width=1000,height=1200' } = {}) {
  const lh = await getLetterhead();
  const header = letterheadHeader(lh, title, subtitle, extra);
  let out = html.replace('</head>', `<style>${lh.css || ''}</style></head>`);
  if (out.includes('<!--LH-->')) out = out.replace('<!--LH-->', lh.header_html || '');
  else if (out.includes('<!--LETTERHEAD-->')) out = out.replace('<!--LETTERHEAD-->', header);
  else out = out.replace(/<body([^>]*)>/i, `<body$1>${header}`);
  const w = window.open('', '_blank', features);
  if (!w) return null;
  w.document.open(); w.document.write(out); w.document.close();
  return w;
}

export function clearBrandingCache() {
  _brandingCache = null;
  _brandingTs = 0;
}

const escapeHtml = (s) => String(s ?? '').replace(/[&<>"']/g, c => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
}[c]));

/**
 * Build the branded print HTML.
 * @param {Object} opts
 * @param {string} opts.title         Big top-right title (e.g. "DISPOSAL TICKET")
 * @param {string} [opts.subtitle]    Smaller subtitle / number under the title
 * @param {string} opts.body          Inner HTML for the document body
 * @param {string} [opts.orientation] "portrait" | "landscape" (default portrait)
 * @param {string} [opts.pageSize]    "Letter" | "A4" | "4in 6in" (default Letter)
 * @param {string} [opts.qrUrl]       If provided, renders a QR in the footer
 * @param {string} [opts.statusBadge] HTML status badge to render under the title
 * @param {string} [opts.footerNote]  Right-side small text in the footer
 * @param {Object} [opts.branding]    Optional pre-loaded branding (skips fetch)
 */
export async function buildBrandedPrintHtml(opts) {
  const branding = opts.branding || await getBrandingCached();
  const lh = await getLetterhead();
  const orgName = branding.app_name || 'BOM Maestro';
  const tagline = branding.tagline || '';
  const orgLogo = branding.logo_data || branding.logo_data_url || branding.logo_url || '';
  const primary = branding.primary_color || '#1e3a8a';
  const orientation = opts.orientation || 'portrait';
  const pageSize = opts.pageSize || 'Letter';
  const qr = opts.qrUrl
    ? `<img src="https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(opts.qrUrl)}" width="120" height="120" alt="QR" />`
    : '';

  return `<!doctype html><html><head>
    <title>${escapeHtml(opts.title || orgName)}</title>
    <meta charset="utf-8" />
    <style>
      ${lh.css || ''}
      @page { size: ${pageSize} ${orientation}; }
      * { box-sizing: border-box; }
      body { font-family: 'Helvetica Neue', Arial, sans-serif; margin: 0; color: #1f2937; }
      .branded-doc { max-width: 100%; }
      .doc-header { display: flex; justify-content: space-between; align-items: flex-start;
                    border-bottom: 3px solid ${primary}; padding-bottom: 14pt; margin-bottom: 18pt; }
      .doc-header .org img { max-height: 64px; max-width: 240px; background: #ffffff; padding: 4px 8px; border-radius: 4px; }
      .doc-header .org .name { font-size: 18pt; font-weight: 700; color: ${primary}; letter-spacing: 0.02em; }
      .doc-header .org .tag { margin-top: 4pt; font-size: 10pt; color: #6b7280; }
      .doc-header .meta { text-align: right; }
      .doc-header h1 { margin: 0; font-size: 22pt; color: ${primary}; letter-spacing: 0.04em; }
      .doc-header .sub { font-family: 'SF Mono', Monaco, Consolas, monospace; color: #4b5563; font-size: 11pt; margin-top: 4pt; }
      .badge { display: inline-block; padding: 3pt 8pt; border-radius: 3px; font-size: 9pt;
               text-transform: uppercase; letter-spacing: 0.04em; font-weight: 600; margin-top: 6pt; }
      .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 18pt; }
      .grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 14pt; }
      .label { color: #6b7280; text-transform: uppercase; font-size: 8pt; letter-spacing: 0.06em; margin-bottom: 2pt; }
      .value { font-size: 11pt; margin-bottom: 10pt; }
      .mono { font-family: 'SF Mono', Monaco, Consolas, monospace; }
      table.report { width: 100%; border-collapse: collapse; margin: 10pt 0; }
      table.report th { background: ${primary}; color: white; text-align: left; padding: 6pt 8pt; font-size: 9pt;
                        text-transform: uppercase; letter-spacing: 0.04em; }
      table.report td { padding: 5pt 8pt; border-bottom: 1px solid #e5e7eb; font-size: 10pt; }
      table.report tr:nth-child(even) td { background: #f9fafb; }
      .section-title { font-size: 13pt; color: ${primary}; font-weight: 700; margin: 18pt 0 8pt 0;
                       border-bottom: 1px solid #e5e7eb; padding-bottom: 4pt; }
      .callout { padding: 12pt; border-radius: 6px; background: #f3f4f6; border-left: 4px solid ${primary}; margin: 10pt 0; }
      .doc-footer { margin-top: 24pt; padding-top: 10pt; border-top: 1px solid #e5e7eb;
                    display: flex; justify-content: space-between; align-items: center; font-size: 8pt; color: #6b7280; }
      .doc-footer .qr { text-align: center; }
      .signoff { display: grid; grid-template-columns: 1fr 1fr; gap: 30pt; margin-top: 30pt; }
      .signoff .line { border-top: 1px solid #9ca3af; padding-top: 6pt; font-size: 9pt; color: #4b5563; }
      .total-box { margin-top: 12pt; padding: 14pt; border: 2px solid ${primary}; border-radius: 6px;
                   background: rgba(0,0,0,0.02); }
      .total-box .total { font-size: 24pt; font-weight: 700; color: ${primary}; font-family: 'SF Mono', Monaco, Consolas, monospace; }
      @media print { .no-print { display: none !important; } body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
    </style></head><body>
    <div class="branded-doc">
      <div class="doc-header">
        <div class="org">
          ${lh.header_html || (orgLogo ? `<img src="${orgLogo}" alt="${escapeHtml(orgName)}" />` : `<div class="name">${escapeHtml(orgName)}</div>`)}
          ${!lh.header_html && tagline ? `<div class="tag">${escapeHtml(tagline)}</div>` : ''}
        </div>
        <div class="meta">
          <h1>${escapeHtml(opts.title || '')}</h1>
          ${opts.subtitle ? `<div class="sub">${escapeHtml(opts.subtitle)}</div>` : ''}
          ${opts.statusBadge || ''}
        </div>
      </div>
      ${opts.body || ''}
      ${(opts.footerNote || qr) ? `<div class="doc-footer">
        <div>
          ${opts.footerNote ? `<div>${opts.footerNote}</div>` : ''}
        </div>
        ${qr ? `<div class="qr">${qr}<div style="font-size:7pt; margin-top:2pt;">Scan for live status</div></div>` : ''}
      </div>` : ''}
    </div>
    <script>window.onload = () => setTimeout(() => window.print(), 350);</script>
  </body></html>`;
}

/** Open a new window pre-populated with the branded HTML and trigger print. */
export async function openBrandedPrintWindow(opts) {
  const html = await buildBrandedPrintHtml(opts);
  const w = window.open('', '_blank', `width=${opts.orientation === 'landscape' ? 1100 : 900},height=1100`);
  if (!w) {
    // Fall back to in-page preview that user can print manually
    const div = document.createElement('div');
    div.innerHTML = '<a href="#" style="position:fixed;top:10px;right:10px;background:#fff;padding:6px 10px;border:1px solid #ccc;z-index:9999">Pop-ups blocked — please allow pop-ups to print</a>';
    document.body.appendChild(div);
    setTimeout(() => div.remove(), 5000);
    return null;
  }
  w.document.open();
  w.document.write(html);
  w.document.close();
  return w;
}

export const PrintHelpers = {
  /** Wraps a 2-column key/value grid */
  kvGrid: (rows) => `<div class="grid-2">${rows.map(([label, value]) => `
    <div><div class="label">${escapeHtml(label)}</div><div class="value">${value ?? '—'}</div></div>
  `).join('')}</div>`,
  /** Single key/value row */
  kv: (label, value, mono = false) => `
    <div class="label">${escapeHtml(label)}</div>
    <div class="value${mono ? ' mono' : ''}">${value ?? '—'}</div>`,
  /** Build a report table. cols=[{key,label,fmt}] rows=array of objects */
  table: (cols, rows) => `<table class="report"><thead><tr>${cols.map(c => `<th>${escapeHtml(c.label || c.key)}</th>`).join('')}</tr></thead><tbody>
    ${rows.map(r => `<tr>${cols.map(c => `<td${c.mono ? ' class="mono"' : ''}>${c.fmt ? c.fmt(r[c.key], r) : escapeHtml(r[c.key] ?? '')}</td>`).join('')}</tr>`).join('')}
  </tbody></table>`,
  sectionTitle: (text) => `<div class="section-title">${escapeHtml(text)}</div>`,
  callout: (html) => `<div class="callout">${html}</div>`,
  totalBox: (label, amount) => `<div class="total-box"><div class="label">${escapeHtml(label)}</div><div class="total">${amount}</div></div>`,
  signoffLines: (left, right) => `<div class="signoff"><div class="line">${escapeHtml(left)}</div><div class="line">${escapeHtml(right)}</div></div>`,
  statusBadge: (status, color) => `<span class="badge" style="background:${color}1a; color:${color};">${escapeHtml(status)}</span>`,
  escape: escapeHtml,
};

export { escapeHtml };
